import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TablePagination,
  CircularProgress,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const WeeklyReportTable = ({ reportData, loading, error }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [expandedRows, setExpandedRows] = useState({});
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!reportData) return [];

    return [...reportData].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case "Unit Name":
          aValue = a.unitName;
          bValue = b.unitName;
          break;
        case "Year":
          aValue = a.year;
          bValue = b.year;
          break;
        case "Week":
          aValue = a.week;
          bValue = b.week;
          break;
        case "Stop(Hrs)":
          aValue = a.down;
          bValue = b.down;
          break;
        case "Run(Hrs)":
          aValue = a.up;
          bValue = b.up;
          break;
        case "Customer(Hrs)":
          aValue = a.customer;
          bValue = b.customer;
          break;
        case "PRC(Hrs)":
          aValue = a.precision;
          bValue = b.precision;
          break;
        case "Shutdowns":
          aValue = a.shutDowns;
          bValue = b.shutDowns;
          break;
        default:
          return 0;
      }

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [reportData, order, orderBy]);

  const paginated = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const columns = [
    { label: "Unit Name", hideOnMobile: false },
    { label: "Year", hideOnMobile: false },
    { label: "Week", hideOnMobile: true },
    { label: "Stop(Hrs)", hideOnMobile: true },
    { label: "Run(Hrs)", hideOnMobile: true },
    { label: "Customer(Hrs)", hideOnMobile: true },
    { label: "PRC(Hrs)", hideOnMobile: true },
    { label: "Shutdowns", hideOnMobile: true },
  ];

  if (loading) {
    return (
      <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{ py: 4, textAlign: "center", color: "red", fontWeight: "bold" }}
      >
        {error}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Typography fontWeight="bold" sx={{ fontSize: 18, mb: 1, px: 2 }}>
        Weekly Runtime Report
      </Typography>

      <TableContainer sx={{ pl: 2, height: "63vh" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  display: { xs: "table-cell", md: "none" },
                  textAlign: "center",
                  width: 50,
                }}
              >
                Details
              </TableCell>

              {columns.map(({ label, hideOnMobile }) => (
                <TableCell
                  key={label}
                  sx={{
                    display: hideOnMobile
                      ? { xs: "none", md: "table-cell" }
                      : "table-cell",
                  }}
                >
                  <TableSortLabel
                    active={orderBy === label}
                    direction={orderBy === label ? order : "asc"}
                    onClick={() => handleRequestSort(label)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((row, index) => (
                <React.Fragment key={row.unitId}>
                  <TableRow
                    hover
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#2f3136" : "#3a3c43",
                    }}
                  >
                    {/* Expand button mobile */}
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        textAlign: "center",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => toggleRowExpansion(row.unitId)}
                        sx={{ color: "white" }}
                      >
                        {expandedRows[row.unitId] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>{row.unitName}</TableCell>
                    <TableCell>{row.year}</TableCell>

                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.week}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.down}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.up}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.customer}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.precision}
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", md: "table-cell" } }}
                    >
                      {row.shutDowns}
                    </TableCell>
                  </TableRow>

                  {/* Mobile collapse */}
                  <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                    <TableCell colSpan={3} sx={{ p: 0 }}>
                      <Collapse in={expandedRows[row.unitId]} unmountOnExit>
                        <Box sx={{ p: 2 }}>
                          <Typography>Week: {row.week}</Typography>
                          <Typography>Stop: {row.down}</Typography>
                          <Typography>Run: {row.up}</Typography>
                          <Typography>Customer: {row.customer}</Typography>
                          <Typography>PRC: {row.precision}</Typography>
                          <Typography>Shutdowns: {row.shutDowns}</Typography>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  No data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        sx={{ mt: 1 }}
      />
    </Box>
  );
};

export default WeeklyReportTable;
